import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { User, Mail, Shield, Lock, Pencil, RefreshCw, Eye, EyeOff, Check, X } from 'lucide-react';

const generarPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let pass = '';
    for (let i = 0; i < 12; i++) pass += chars[Math.floor(Math.random() * chars.length)];
    return pass;
};

const Perfil = () => {
    const { user, refreshUser } = useAuth();
    const [editingName, setEditingName] = useState(false);
    const [nombre, setNombre] = useState(user?.nombre || '');
    const [savingName, setSavingName] = useState(false);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [savingPass, setSavingPass] = useState(false);

    const handleSaveName = async () => {
        if (!nombre.trim()) {
            toast.error('El nombre es requerido');
            return;
        }
        setSavingName(true);
        try {
            await authAPI.updateProfile(nombre.trim());
            toast.success('Nombre actualizado');
            setEditingName(false);
            if (refreshUser) refreshUser();
        } catch (err) {
            toast.error(err.message || 'Error al actualizar nombre');
        } finally {
            setSavingName(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!newPassword) {
            toast.error('Ingresa una contraseña');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('Mínimo 6 caracteres');
            return;
        }
        if (confirmPassword && newPassword !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        setSavingPass(true);
        try {
            await authAPI.changePassword(newPassword);
            toast.success('Contraseña actualizada correctamente');
            setNewPassword('');
            setConfirmPassword('');
            setShowPassword(false);
        } catch (err) {
            toast.error(err.message || 'Error al cambiar contraseña');
        } finally {
            setSavingPass(false);
        }
    };

    const handleGenerar = () => {
        const pass = generarPassword();
        setNewPassword(pass);
        setConfirmPassword('');
        setShowPassword(true);
    };

    const rolLabels = { superadmin: 'Super Admin', admin: 'Admin', comercial: 'Comercial', personal: 'Personal' };
    const rolLabel = rolLabels[user?.rol] || user?.rol;

    return (
        <div className="card">
            <div className="card-header">
                <h2>Mi Perfil</h2>
            </div>

            <div className="admin-content">
                <div className="perfil-info">
                    <div className="perfil-avatar">
                        <User size={48} />
                    </div>
                    <div className="perfil-datos">
                        <div className="perfil-field">
                            <User size={16} />
                            <span className="perfil-label">Nombre</span>
                            {editingName ? (
                                <div className="perfil-edit-row">
                                    <input
                                        className="input"
                                        type="text"
                                        value={nombre}
                                        onChange={(e) => setNombre(e.target.value)}
                                        autoFocus
                                    />
                                    <button className="btn-icon edit" onClick={handleSaveName} disabled={savingName} title="Guardar">
                                        <Check size={16} />
                                    </button>
                                    <button className="btn-icon text-danger" onClick={() => { setEditingName(false); setNombre(user?.nombre || ''); }} title="Cancelar">
                                        <X size={16} />
                                    </button>
                                </div>
                            ) : (
                                <span className="perfil-value">
                                    {user?.nombre}
                                    <button className="btn-icon edit" onClick={() => setEditingName(true)} title="Editar nombre" style={{ marginLeft: '8px' }}>
                                        <Pencil size={14} />
                                    </button>
                                </span>
                            )}
                        </div>
                        <div className="perfil-field">
                            <Mail size={16} />
                            <span className="perfil-label">Email</span>
                            <span className="perfil-value">{user?.email}</span>
                        </div>
                        <div className="perfil-field">
                            <Shield size={16} />
                            <span className="perfil-label">Rol</span>
                            <span className={`badge ${user?.rol === 'superadmin' ? 'warning' : user?.rol === 'admin' ? 'success' : 'info'}`}>
                                {rolLabel}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="divider" />

                <h3 className="h3"><Lock size={18} /> Cambiar Contraseña</h3>
                <form onSubmit={handleChangePassword}>
                    <div className="form-row">
                        <label className="field" style={{ flex: 1 }}>
                            <span className="label">Nueva contraseña *</span>
                            <div className="input-password-wrap">
                                <input
                                    className="input"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </label>
                        <label className="field" style={{ flex: 1 }}>
                            <span className="label">Confirmar contraseña</span>
                            <input
                                className="input"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repite la contraseña"
                            />
                        </label>
                        <button
                            type="button"
                            className="btn ghost"
                            onClick={handleGenerar}
                            style={{ alignSelf: 'flex-end' }}
                            title="Generar contraseña segura"
                        >
                            <RefreshCw size={16} /> Generar
                        </button>
                    </div>
                    <button className="btn neon" type="submit" disabled={savingPass} style={{ marginTop: '16px' }}>
                        {savingPass ? 'Guardando...' : 'Cambiar Contraseña'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Perfil;
